import React, { FC, useMemo, useRef, useState } from 'react';
import 'twin.macro';
import useCareerPageContext from 'hooks/context/career-page-context';

import CustomerDropdown from './CustomerDropdown';
import SaveButton from 'components/save-button/SaveButton';
import PageStatusDropDown from './PageStatusDropDown';
import { FormProvider, useForm } from 'react-hook-form';
import { ClockCounterClockwise } from '@phosphor-icons/react';
import ToolTip from 'components/tooltip/ToolTip';
import Button from 'components/button/Button';
import { Modal } from 'components/page/app-page/Modal';
import { Title3 } from 'components/typography/Typography';
import SaveStatus from 'components/save-status/SaveStatus';
import UndoRedo from 'components/undo-redo/UndoRedo';
import {
  COULD_NOT_PUBLISH,
  WaitingChange,
  hourAndMinute,
  waitingLabel,
} from 'api/waitingChanges';
import { CAREERS_NOT_SAVED_YET } from 'context/CareerPageContext';
import { CareerPageStatusTypes } from 'types/careers-page/types';
import * as Styled from './styles';

type Step = 'idle' | 'ask' | 'list' | 'discard' | 'offline';

/**
 * The careers page's header (scene 17, D24). The status is a label; going
 * live and going offline are presses of their own; on an Active page what
 * was changed waits for "Publish changes".
 *
 * The label, "Publish changes (2)", "Discard changes", "Publish page", "Take
 * offline", "Show me what will change?" and the discard question are fixed
 * by the description (section 5c).
 */
const CareersPublish: FC = () => {
  const {
    careerPageConfig,
    waiting,
    publishPage,
    discardPageChanges,
    takePageOffline,
  } = useCareerPageContext();
  const [step, setStep] = useState<Step>('idle');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const working = useRef(false);
  /** The step last shown: its content stays while the pop-up fades out. */
  const shownStep = useRef<Step>(step);
  if (step !== 'idle') shownStep.current = step;
  const shown = shownStep.current;

  const byScreen = useMemo(() => {
    const groups = new Map<string, WaitingChange[]>();
    (waiting?.changes ?? []).forEach((change) => {
      groups.set(change.screen, [...(groups.get(change.screen) ?? []), change]);
    });
    return Array.from(groups.entries());
  }, [waiting]);

  const status = (waiting?.status ??
    careerPageConfig?.status) as CareerPageStatusTypes;
  if (!status || !waiting) return null;

  const active = status === 'ACTIVE';
  const count = active ? waiting.count : 0;
  /** What differs from what visitors see, or last saw (D43). */
  const differs = waiting.changes.length > 0;

  const close = () => {
    setStep('idle');
    setProblem(null);
  };

  const run = (
    act: () => Promise<void>,
    couldNot: string,
    then: () => void = close
  ) => {
    if (working.current) return;
    working.current = true;
    setProblem(null);
    setBusy(true);
    act()
      .then(then, (reason: Error) =>
        setProblem(
          reason?.message === CAREERS_NOT_SAVED_YET
            ? CAREERS_NOT_SAVED_YET
            : couldNot
        )
      )
      .finally(() => {
        working.current = false;
        setBusy(false);
      });
  };

  const publish = () => run(publishPage, COULD_NOT_PUBLISH);
  const discard = () =>
    run(
      discardPageChanges,
      'Could not discard, nothing was changed. Try again.',
      () => undefined
    );
  const offline = () =>
    run(
      takePageOffline,
      'Could not take the page offline, nothing was changed. Try again.'
    );

  return (
    <div tw="flex flex-nowrap items-center gap-2" data-testid="careers-publish">
      <Styled.StatusIndicator status={status} data-testid="careers-status">
        {status === 'ACTIVE' ? 'Active' : status === 'DRAFT' ? 'Draft' : status}
      </Styled.StatusIndicator>

      {status === 'DRAFT' && (
        <Button
          testId="careers-publish-page"
          disabled={busy}
          // D43: a page that was public before says what will change first.
          onClick={() => (differs ? setStep('ask') : publish())}
        >
          Publish page
        </Button>
      )}

      {active && count > 0 && (
        <>
          {/*
            On a strip of its own just under the header: in the row itself
            the label pushed the buttons off the right edge at ordinary
            window widths (seen in the browser, 5 Oct 2026).
          */}
          <div tw="fixed top-[66px] right-[72px] z-10 flex items-center gap-3 bg-white border border-gray-200 rounded-md shadow-sm px-3 py-1">
            <p
              tw="text-xs text-gray-500 whitespace-nowrap"
              data-testid="careers-waiting-label"
            >
              {waitingLabel({ ...waiting, published_at: null })}
            </p>
            <button
              type="button"
              tw="text-xs text-gray-500 font-medium underline whitespace-nowrap"
              data-testid="careers-discard-changes"
              disabled={busy}
              onClick={() => setStep('discard')}
            >
              Discard changes
            </button>
          </div>
          <Button
            testId="careers-publish-changes"
            disabled={busy}
            onClick={() => setStep('ask')}
          >
            {`Publish changes (${count})`}
          </Button>
        </>
      )}

      {active && (
        <button
          type="button"
          tw="text-xs text-gray-500 font-medium underline whitespace-nowrap"
          data-testid="careers-take-offline"
          disabled={busy}
          onClick={() => setStep('offline')}
        >
          Take offline
        </button>
      )}

      {problem && step === 'idle' && (
        <p tw="text-xs text-red-600" data-testid="careers-publish-problem">
          {problem}
        </p>
      )}

      {/* One pop-up whose content follows the step (see PublishChanges). */}
      <Modal show={step !== 'idle'} onClose={close}>
        {shown === 'ask' && (
          <div
            tw="flex flex-col gap-4 w-[32rem] max-w-[80vw] pr-10"
            data-testid="careers-show-what-will-change"
          >
            <Title3>Show me what will change?</Title3>
            {problem && (
              <p
                tw="text-sm text-red-600"
                data-testid="careers-publish-problem"
              >
                {problem}
              </p>
            )}
            <div tw="flex gap-3 justify-end">
              <Button
                variant="secondary"
                testId="careers-publish-now"
                disabled={busy}
                onClick={publish}
              >
                No, publish now
              </Button>
              <Button
                testId="careers-show-the-list"
                onClick={() => setStep('list')}
              >
                Yes
              </Button>
            </div>
          </div>
        )}
        {shown === 'list' && (
          <div
            tw="flex flex-col gap-4 w-[32rem] max-w-[80vw] pr-10"
            data-testid="careers-what-will-change"
          >
            <Title3>What will change</Title3>
            {byScreen.map(([screen, changes]) => (
              <section key={screen} tw="flex flex-col gap-2">
                <h4 tw="text-sm font-semibold text-gray-900">{screen}</h4>
                <ul tw="flex flex-col gap-2">
                  {changes.map((change, place) => (
                    <li
                      // Two sections of one kind give two lines of one name.
                      key={`${screen}-${change.thing}-${place}`}
                      tw="text-sm text-gray-700"
                      data-testid="careers-waiting-change"
                    >
                      <span tw="font-medium">{change.thing}</span>
                      {change.before !== undefined &&
                        change.after !== undefined && (
                          <>
                            <span tw="block text-gray-400 line-through">
                              {change.before}
                            </span>
                            <span tw="block">{change.after}</span>
                          </>
                        )}
                      {waiting.last_change_by && (
                        <span tw="block text-xs text-gray-400">
                          {`${waiting.last_change_by}, ${hourAndMinute(
                            waiting.last_change_at
                          )}`}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            {problem && (
              <p
                tw="text-sm text-red-600"
                data-testid="careers-publish-problem"
              >
                {problem}
              </p>
            )}
            <div tw="flex gap-3 justify-end">
              {active && (
                <Button
                  variant="secondary"
                  testId="careers-list-discard"
                  disabled={busy}
                  onClick={() => setStep('discard')}
                >
                  Discard changes
                </Button>
              )}
              <Button
                variant="secondary"
                testId="careers-list-not-now"
                onClick={close}
              >
                Not now
              </Button>
              <Button
                testId="careers-list-publish"
                disabled={busy}
                onClick={publish}
              >
                Publish
              </Button>
            </div>
          </div>
        )}
        {shown === 'discard' && (
          <div
            tw="flex flex-col gap-4 w-[32rem] max-w-[80vw] pr-10"
            data-testid="careers-discard-question"
          >
            <Title3>Discard changes?</Title3>
            <p tw="text-sm text-gray-700">
              This returns to what is public now. It cannot be undone.
            </p>
            {problem && (
              <p
                tw="text-sm text-red-600"
                data-testid="careers-publish-problem"
              >
                {problem}
              </p>
            )}
            <div tw="flex gap-3 justify-end">
              <Button
                variant="secondary"
                testId="careers-discard-cancel"
                onClick={close}
              >
                Cancel
              </Button>
              <Button
                testId="careers-discard-confirm"
                disabled={busy}
                onClick={discard}
              >
                Discard
              </Button>
            </div>
          </div>
        )}
        {shown === 'offline' && (
          <div
            tw="flex flex-col gap-4 w-[32rem] max-w-[80vw] pr-10"
            data-testid="careers-offline-question"
          >
            <Title3>Take this page offline?</Title3>
            <p tw="text-sm text-gray-700">
              Visitors can no longer open it. Nothing on this screen is lost.
            </p>
            {problem && (
              <p
                tw="text-sm text-red-600"
                data-testid="careers-publish-problem"
              >
                {problem}
              </p>
            )}
            <div tw="flex gap-3 justify-end">
              <Button
                variant="secondary"
                testId="careers-offline-cancel"
                onClick={close}
              >
                Cancel
              </Button>
              <Button
                testId="careers-offline-confirm"
                disabled={busy}
                onClick={offline}
              >
                Take offline
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

const CareerCta: FC = () => {
  const {
    careerPageConfig,
    isPageDirty,
    handleSave,
    setVersionHistoryModalOpen,
    storesItself,
    saveState,
    keptAt,
    switching,
    canUndo,
    canRedo,
    undo,
    redo,
  } = useCareerPageContext();
  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  // An archived page does not store itself; it keeps the controls it had.
  // While a page is still loading neither set is shown.
  const older = careerPageConfig?.status === 'ARCHIVED';

  return (
    <FormProvider {...formMethods}>
      <div tw="flex flex-nowrap items-center">
        <CustomerDropdown />
        {careerPageConfig && (
          <>
            {storesItself ? (
              <div tw="mx-2">
                <CareersPublish />
              </div>
            ) : (
              older && <PageStatusDropDown />
            )}
            <ToolTip text="See the edit history of this page." arrow>
              <div
                tw="mr-1 p-2 cursor-pointer"
                data-testid="careers-versions"
                onClick={() => setVersionHistoryModalOpen(true)}
              >
                <ClockCounterClockwise tw="h-6 w-6 text-gray-800" />
              </div>
            </ToolTip>

            {storesItself ? (
              <>
                <UndoRedo
                  canUndo={canUndo}
                  canRedo={canRedo}
                  onUndo={undo}
                  onRedo={redo}
                />
                <SaveStatus
                  state={saveState}
                  keptAt={keptAt}
                  switching={switching}
                  // This header is full: the line takes only the room it needs.
                  steady={false}
                />
              </>
            ) : (
              older && (
                <SaveButton shouldSave={isPageDirty} onDone={handleSave} />
              )
            )}
          </>
        )}
      </div>
    </FormProvider>
  );
};

export default CareerCta;
